import { useCallback } from 'react';
import { FormSection } from '../ui/FormSection';

export interface ReviewTextFieldsProps {
  title: string;
  body: string;
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  titleMaxLength?: number;
  bodyMinLength?: number;
  bodyMaxLength?: number;
  disabled?: boolean;
  className?: string;
  titleError?: string;
  bodyError?: string;
  required?: boolean;
}

export const ReviewTextFields = ({
  title,
  body,
  onTitleChange,
  onBodyChange,
  titleMaxLength = 120,
  bodyMinLength = 100,
  bodyMaxLength = 20000,
  disabled = false,
  className = '',
  titleError,
  bodyError,
  required = true,
}: ReviewTextFieldsProps) => {
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= titleMaxLength) {
        onTitleChange(newValue);
      }
    },
    [onTitleChange, titleMaxLength]
  );

  const handleBodyChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= bodyMaxLength) {
        onBodyChange(newValue);
      }
    },
    [onBodyChange, bodyMaxLength]
  );

  const titleCharCount = title.length;
  const bodyCharCount = body.length;
  const isTitleNearLimit = titleCharCount >= titleMaxLength * 0.9;
  const isBodyBelowMin = bodyCharCount < bodyMinLength;
  const isBodyNearLimit = bodyCharCount >= bodyMaxLength * 0.9;

  return (
    <FormSection title="Your Review" titleAs="h3" className={className}>
      {/* Title Field */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="review-title" className="block text-sm font-medium text-gray-700">
            Review Title
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <span
            className={`text-xs ${isTitleNearLimit ? 'text-orange-600 font-medium' : 'text-gray-500'}`}
          >
            {titleCharCount}/{titleMaxLength}
          </span>
        </div>
        <input
          id="review-title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          disabled={disabled}
          placeholder="Summarize your experience in a few words"
          className={`
            w-full px-3 py-2 border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'}
            ${titleError ? 'border-red-500' : 'border-gray-300'}
          `}
          aria-describedby={titleError ? 'title-error' : undefined}
        />
        {titleError && (
          <p id="title-error" className="mt-1 text-sm text-red-600" role="alert">
            {titleError}
          </p>
        )}
      </div>

      {/* Body Field */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="review-body" className="block text-sm font-medium text-gray-700">
            Your Review
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <span
            className={`text-xs ${
              isBodyBelowMin
                ? 'text-red-600 font-medium'
                : isBodyNearLimit
                  ? 'text-orange-600 font-medium'
                  : 'text-gray-500'
            }`}
          >
            {bodyCharCount}/{bodyMaxLength}
            {isBodyBelowMin && ` (min ${bodyMinLength})`}
          </span>
        </div>
        <textarea
          id="review-body"
          value={body}
          onChange={handleBodyChange}
          disabled={disabled}
          rows={6}
          placeholder={`Share your experience in detail. What did you enjoy? What could be improved? (minimum ${bodyMinLength} characters)`}
          className={`
            w-full px-3 py-2 border rounded-lg resize-y
            focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'}
            ${bodyError ? 'border-red-500' : 'border-gray-300'}
          `}
          aria-describedby={bodyError ? 'body-error' : undefined}
        />
        {bodyError && (
          <p id="body-error" className="mt-1 text-sm text-red-600" role="alert">
            {bodyError}
          </p>
        )}
        {!bodyError && isBodyBelowMin && (
          <p className="mt-1 text-sm text-gray-500">
            {bodyMinLength - bodyCharCount} more characters needed
          </p>
        )}
      </div>
    </FormSection>
  );
};
